import { create } from 'zustand';
import { saveToJSON, loadFromJSON } from '../utils/serializer';
import { useDiagramStore } from './diagramStore';
import { snapshotFromStore } from './undoStore';
import { DiagramType } from '../types';

const PROJECTS_KEY = 'velostruct-projects';
const ACTIVE_PROJECT_KEY = 'velostruct-active-project';
const MAX_CHECKPOINTS = 5;

export interface Checkpoint {
  id: string;
  label: string;
  createdAt: number;
  json: string; // full serialized diagram JSON
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  createdAt: number;
  updatedAt: number;
  json: string; // serialized diagram JSON
  checkpoints: Checkpoint[];
  nodeCount?: number;
  edgeCount?: number;
}

interface ProjectStore {
  projects: Project[];
  activeProjectId: string | null;

  // Actions
  loadFromStorage: () => void;
  saveActiveProject: () => void;
  createProject: (name: string, diagramType?: DiagramType) => string; // returns new project id
  deleteProject: (id: string) => void;
  renameProject: (id: string, name: string) => void;
  updateDescription: (id: string, description: string) => void;
  switchProject: (id: string) => void;
  importProjectFromJson: (name: string, json: string) => string;

  // Checkpoints
  addCheckpoint: (label: string) => void;
  restoreCheckpoint: (projectId: string, checkpointId: string) => void;
  deleteCheckpoint: (projectId: string, checkpointId: string) => void;
}

function persistProjects(projects: Project[]) {
  try {
    localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
  } catch {
    // quota exceeded
  }
}

function readProjects(): Project[] {
  try {
    const raw = localStorage.getItem(PROJECTS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Project[];
  } catch {
    return [];
  }
}

function areGraphsEmpty(graphs?: Record<string, { nodes: unknown[]; edges: unknown[] }>) {
  return Object.values(graphs ?? {}).every(graph => graph.nodes.length === 0 && graph.edges.length === 0);
}

function isPageEmpty(page: {
  nodes?: unknown[];
  edges?: unknown[];
  groups?: unknown[];
  participants?: unknown[];
  messages?: unknown[];
  fragments?: unknown[];
  participantGroups?: unknown[];
  graphs?: Record<string, { nodes: unknown[]; edges: unknown[] }>;
}) {
  return (
    (page.nodes?.length ?? 0) === 0 &&
    (page.edges?.length ?? 0) === 0 &&
    (page.groups?.length ?? 0) === 0 &&
    (page.participants?.length ?? 0) === 0 &&
    (page.messages?.length ?? 0) === 0 &&
    (page.fragments?.length ?? 0) === 0 &&
    (page.participantGroups?.length ?? 0) === 0 &&
    areGraphsEmpty(page.graphs)
  );
}

function captureCurrentProjectSnapshot() {
  const st = useDiagramStore.getState();
  const json = saveToJSON({
    diagramType: st.diagramType,
    participants: st.participants,
    messages: st.messages,
    fragments: st.fragments,
    participantGroups: st.participantGroups,
    nodes: st.nodes,
    edges: st.edges,
    viewport: st.viewport,
    layers: st.layers,
    groups: st.groups,
    graphs: st.graphs,
    activeGraphId: st.activeGraphId,
    pages: st.pages,
    activePage: st.activePage,
    catalog: st.catalog,
  });

  const hasLiveContent =
    st.nodes.length > 0 ||
    st.edges.length > 0 ||
    st.groups.length > 0 ||
    st.participants.length > 0 ||
    st.messages.length > 0 ||
    st.fragments.length > 0 ||
    st.participantGroups.length > 0 ||
    !areGraphsEmpty(st.graphs);

  const hasPageContent = st.pages.some(page => !isPageEmpty(page));

  return {
    json,
    nodeCount: st.nodes.length,
    edgeCount: st.edges.length,
    isEmpty: !(hasLiveContent || hasPageContent),
  };
}

function syncOrPruneActiveProject(projects: Project[], activeProjectId: string | null) {
  if (!activeProjectId) return projects;

  const snapshot = captureCurrentProjectSnapshot();
  if (snapshot.isEmpty) {
    return projects.filter(project => project.id !== activeProjectId);
  }

  return projects.map(project =>
    project.id === activeProjectId
      ? {
          ...project,
          json: snapshot.json,
          updatedAt: Date.now(),
          nodeCount: snapshot.nodeCount,
          edgeCount: snapshot.edgeCount,
        }
      : project
  );
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  projects: [],
  activeProjectId: null,

  loadFromStorage: () => {
    const projects = readProjects();
    const activeId = localStorage.getItem(ACTIVE_PROJECT_KEY);
    set({ projects, activeProjectId: activeId });
  },

  saveActiveProject: () => {
    const { activeProjectId } = get();
    if (!activeProjectId) return;

    const snapshot = captureCurrentProjectSnapshot();

    const updated = get().projects.map(p =>
      p.id === activeProjectId
        ? {
            ...p,
            json: snapshot.json,
            updatedAt: Date.now(),
            nodeCount: snapshot.nodeCount,
            edgeCount: snapshot.edgeCount,
          }
        : p
    );
    persistProjects(updated);
    set({ projects: updated });
  },

  createProject: (name: string, diagramType = 'flow') => {
    const preparedProjects = syncOrPruneActiveProject(get().projects, get().activeProjectId);
    const id = `project-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const now = Date.now();

    snapshotFromStore();
    useDiagramStore.getState().clearDiagram(diagramType);
    const blankSnapshot = captureCurrentProjectSnapshot();

    const newProject: Project = {
      id,
      name,
      createdAt: now,
      updatedAt: now,
      json: blankSnapshot.json,
      checkpoints: [],
      nodeCount: blankSnapshot.nodeCount,
      edgeCount: blankSnapshot.edgeCount,
    };
    const projects = [...preparedProjects, newProject];
    persistProjects(projects);
    localStorage.setItem(ACTIVE_PROJECT_KEY, id);
    set({ projects, activeProjectId: id });
    return id;
  },

  deleteProject: (id: string) => {
    const { activeProjectId } = get();
    const updated = get().projects.filter(p => p.id !== id);
    persistProjects(updated);

    const newActiveId = activeProjectId === id
      ? (updated[updated.length - 1]?.id ?? null)
      : activeProjectId;

    if (newActiveId) localStorage.setItem(ACTIVE_PROJECT_KEY, newActiveId);
    else localStorage.removeItem(ACTIVE_PROJECT_KEY);

    set({ projects: updated, activeProjectId: newActiveId });

    // If we deleted the active project, load the new active one
    if (activeProjectId === id && newActiveId) {
      const next = updated.find(p => p.id === newActiveId);
      if (next?.json) {
        try {
          snapshotFromStore();
          useDiagramStore.getState().loadDiagram(loadFromJSON(next.json));
        } catch { /* ignore */ }
      } else {
        useDiagramStore.getState().clearDiagram();
      }
    }
  },

  renameProject: (id: string, name: string) => {
    const updated = get().projects.map(p =>
      p.id === id ? { ...p, name, updatedAt: Date.now() } : p
    );
    persistProjects(updated);
    set({ projects: updated });
  },

  updateDescription: (id: string, description: string) => {
    const updated = get().projects.map(p =>
      p.id === id ? { ...p, description } : p
    );
    persistProjects(updated);
    set({ projects: updated });
  },

  switchProject: (id: string) => {
    const { projects, activeProjectId } = get();
    if (id === activeProjectId) return;

    const updatedProjects = syncOrPruneActiveProject(projects, activeProjectId);
    const target = updatedProjects.find(p => p.id === id);
    if (!target) return;

    persistProjects(updatedProjects);
    localStorage.setItem(ACTIVE_PROJECT_KEY, id);
    set({ projects: updatedProjects, activeProjectId: id });

    snapshotFromStore();
    if (target.json) {
      try {
        useDiagramStore.getState().loadDiagram(loadFromJSON(target.json));
      } catch { /* ignore */ }
    } else {
      useDiagramStore.getState().clearDiagram();
    }
  },

  importProjectFromJson: (name: string, json: string) => {
    const id = `project-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const now = Date.now();
    const newProject: Project = {
      id,
      name,
      createdAt: now,
      updatedAt: now,
      json,
      checkpoints: [],
    };
    const projects = [...get().projects, newProject];
    persistProjects(projects);
    localStorage.setItem(ACTIVE_PROJECT_KEY, id);

    snapshotFromStore();
    try {
      useDiagramStore.getState().loadDiagram(loadFromJSON(json));
    } catch { /* ignore */ }

    set({ projects, activeProjectId: id });
    return id;
  },

  addCheckpoint: (label: string) => {
    const { activeProjectId } = get();
    if (!activeProjectId) return;

    // First save latest diagram state to project
    get().saveActiveProject();

    const project = get().projects.find(p => p.id === activeProjectId);
    if (!project) return;

    const checkpoint: Checkpoint = {
      id: `cp-${Date.now()}`,
      label,
      createdAt: Date.now(),
      json: project.json,
    };

    // Keep only last MAX_CHECKPOINTS
    const checkpoints = [...project.checkpoints, checkpoint].slice(-MAX_CHECKPOINTS);
    const updated = get().projects.map(p =>
      p.id === activeProjectId ? { ...p, checkpoints } : p
    );
    persistProjects(updated);
    set({ projects: updated });
  },

  restoreCheckpoint: (projectId: string, checkpointId: string) => {
    const project = get().projects.find(p => p.id === projectId);
    const cp = project?.checkpoints.find(c => c.id === checkpointId);
    if (!cp) return;

    snapshotFromStore();
    try {
      useDiagramStore.getState().loadDiagram(loadFromJSON(cp.json));
    } catch { /* ignore */ }

    // Update project json to match checkpoint
    const updated = get().projects.map(p =>
      p.id === projectId ? { ...p, json: cp.json, updatedAt: Date.now() } : p
    );
    persistProjects(updated);
    set({ projects: updated });
  },

  deleteCheckpoint: (projectId: string, checkpointId: string) => {
    const updated = get().projects.map(p =>
      p.id === projectId
        ? { ...p, checkpoints: p.checkpoints.filter(c => c.id !== checkpointId) }
        : p
    );
    persistProjects(updated);
    set({ projects: updated });
  },
}));
